import { createMiddleware } from 'redux-beacon';
import { GoogleTagManager } from 'redux-beacon/targets/google-tag-manager';

import { LOCATION_CHANGE } from 'react-router-redux';
import {
  ANALYTICS_ADD_TO_CART,
  ANALYTICS_BEGIN_CHECKOUT,
  ANALYTICS_CLEAR_ECOMMERCE,
  ANALYTICS_PAYMENT_INFO,
  ANALYTICS_PRODUCT_DETAILS_VIEW,
  ANALYTICS_PRODUCT_REMOVED,
  ANALYTICS_PURCHASE,
  ANALYTICS_SELECT_CALENDAR_ITEM,
  ANALYTICS_SELECT_ITEM,
  ANALYTICS_SELECT_LIMITED_ITEM,
  ANALYTICS_SELECT_RELATED_ITEM,
  ANALYTICS_SELECT_SPECIAL_ITEM,
  ANALYTICS_VIEW_CALENDAR_ITEM_LIST,
  ANALYTICS_VIEW_CART,
  ANALYTICS_VIEW_ITEM_LIST,
  ANALYTICS_VIEW_LIMITED_ITEM_LIST,
  ANALYTICS_VIEW_ORDER_DETAILS,
  ANALYTICS_VIEW_PRODUCT_OR_CATEGORY,
  ANALYTICS_VIEW_SPECIAL_ITEM_LIST,
} from './actions';

const selectCurrency = ({ channels }) =>
  channels.availableChannels[channels.activeChannel].baseCurrency;
const selectCurrencyCode = (state) => selectCurrency(state).code;

const transformPrice = (price, state) => {
  const currency = selectCurrency(state);
  const parsedPrice = parseFloat(price / 10 ** currency.fractionDigits);
  return parsedPrice.toFixed(currency.fractionDigits);
};

const transformListItem = (item, index, state) => ({
  item_id: item.code,
  item_name: item.name,
  price: transformPrice(item.price, state),
  ...createCategories(item.mainTaxon),
  quantity: 1,
  index,
});

const transformCartProduct = (cartProduct, state) => ({
  item_id: cartProduct.variant.product.code,
  item_name: cartProduct.variant.product.name,
  price: transformPrice(cartProduct.unitPrice, state),
  ...createCategories(cartProduct.variant.product.mainTaxon),
  quantity: cartProduct.quantity,
});

const makeTaxonList = ({ code, level, parent }) => [
  ...(level > 1 && parent ? makeTaxonList(parent) : []),
  code,
];

const createCategories = (mainTaxon) => {
  const categories = makeTaxonList(mainTaxon);

  return categories.reduce((acc, category, idx) => {
    acc[`item_category${idx + 1 > 1 ? idx + 1 : ''}`] = category;

    return acc;
  }, {});
};

const eventsMap = {
  [LOCATION_CHANGE]: ({ payload }) =>
    payload.action === 'PUSH' && {
      hitType: 'pageview',
      page: payload.pathname,
    },
  [ANALYTICS_CLEAR_ECOMMERCE]: () => ({
    ecommerce: null,
  }),
  [ANALYTICS_VIEW_LIMITED_ITEM_LIST]: ({ payload }, prevState, nextState) => ({
    event: 'view_item_list',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `limited_offers_${payload.taxon.code}`,
      item_list_name: `Limited Offers ${payload.taxon.name}`,
      items: payload.products.map((product, idx) => transformListItem(product, idx, nextState)),
    },
  }),
  [ANALYTICS_VIEW_CALENDAR_ITEM_LIST]: ({ payload }, prevState, nextState) => ({
    event: 'view_item_list',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `calendar_${payload.taxon.code}`,
      item_list_name: `Calendar ${payload.taxon.name}`,
      items: payload.products.map((product, idx) => transformListItem(product, idx, nextState)),
    },
  }),
  [ANALYTICS_VIEW_SPECIAL_ITEM_LIST]: ({ payload }, prevState, nextState) => ({
    event: 'view_item_list',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `special_offers_${payload.taxon.code}`,
      item_list_name: `Special Offers ${payload.taxon.name}`,
      items: payload.products.map((product, idx) => transformListItem(product, idx, nextState)),
    },
  }),
  [ANALYTICS_VIEW_ITEM_LIST]: ({ payload }, prevState, nextState) => ({
    event: 'view_item_list',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: payload.taxon.code,
      item_list_name: payload.taxon.name,
      items: payload.products.map((product, idx) => transformListItem(product, idx, nextState)),
    },
  }),
  [ANALYTICS_SELECT_LIMITED_ITEM]: ({ payload }, prevState, nextState) => ({
    event: 'select_item',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `limited_offers_${payload.taxon.code}`,
      item_list_name: `Limited Offers ${payload.taxon.name}`,
      items: [transformListItem(payload.product, payload.index, nextState)],
    },
  }),
  [ANALYTICS_SELECT_CALENDAR_ITEM]: ({ payload }, prevState, nextState) => ({
    event: 'select_item',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `calendar_${payload.taxon.code}`,
      item_list_name: `Calendar ${payload.taxon.name}`,
      items: [transformListItem(payload.product, payload.index, nextState)],
    },
  }),
  [ANALYTICS_SELECT_SPECIAL_ITEM]: ({ payload }, prevState, nextState) => ({
    event: 'select_item',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `special_offers_${payload.taxon.code}`,
      item_list_name: `Special Offers ${payload.taxon.name}`,
      items: [transformListItem(payload.product, payload.index, nextState)],
    },
  }),
  [ANALYTICS_SELECT_ITEM]: ({ payload }, prevState, nextState) => ({
    event: 'select_item',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: payload.taxon.code,
      item_list_name: payload.taxon.name,
      items: [transformListItem(payload.product, payload.index, nextState)],
    },
  }),
  [ANALYTICS_SELECT_RELATED_ITEM]: ({ payload }, prevState, nextState) => ({
    event: 'select_item',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      item_list_id: `related_products_${payload.product.code}`,
      item_list_name: `Related products for ${payload.product.name}`,
      items: [transformListItem(payload.relatedProduct, payload.index, nextState)],
    },
  }),
  [ANALYTICS_PRODUCT_DETAILS_VIEW]: ({ payload }, prevState, nextState) => {
    const item = {
      item_id: payload.code,
      item_name: payload.name,
      ...createCategories(payload.mainTaxon),
      price: transformPrice(payload.price, nextState),
      quantity: 1,
    };

    return {
      event: 'view_item',
      ...(nextState.user.profile &&
        nextState.user.profile.id && { user_id: nextState.user.profile.id }),
      channel: nextState.channels.activeChannel,
      ecommerce: {
        items: [item],
      },
    };
  },
  [ANALYTICS_ADD_TO_CART]: ({ payload }, prevState, nextState) => ({
    event: 'add_to_cart',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      ...(nextState.cart &&
        nextState.cart.promotionCoupon &&
        nextState.cart.promotionCoupon.code && { coupon: nextState.cart.promotionCoupon.code }),
      currency: selectCurrencyCode(nextState),
      value: transformPrice(payload.total, nextState),
      items: payload.items ? payload.items.map((cartProduct) => transformCartProduct(cartProduct, nextState)) : [transformCartProduct(payload, nextState)],
    },
  }),
  [ANALYTICS_PRODUCT_REMOVED]: ({ payload }, prevState, nextState) => ({
    event: 'remove_from_cart',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      ...(nextState.cart &&
        nextState.cart.promotionCoupon &&
        nextState.cart.promotionCoupon.code && { coupon: nextState.cart.promotionCoupon.code }),
      items: [transformCartProduct(payload, nextState)],
    },
  }),
  [ANALYTICS_VIEW_CART]: ({ payload }, prevState, nextState) => ({
    event: 'view_cart',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      ...(nextState.cart &&
        nextState.cart.promotionCoupon &&
        nextState.cart.promotionCoupon.code && { coupon: nextState.cart.promotionCoupon.code }),
      currency: selectCurrencyCode(nextState),
      value: transformPrice(payload.total, nextState),
      items: payload.items.map((cartProduct) => transformCartProduct(cartProduct, nextState)),
    },
  }),
  [ANALYTICS_PAYMENT_INFO]: ({ payload }, prevState, nextState) => ({
    event: 'add_payment_info',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      ...(nextState.cart &&
        nextState.cart.promotionCoupon &&
        nextState.cart.promotionCoupon.code && { coupon: nextState.cart.promotionCoupon.code }),
      currency: selectCurrencyCode(nextState),
      payment_type: payload.method.code,
      items: payload.items.map((cartProduct) => transformCartProduct(cartProduct, nextState)),
    },
  }),
  [ANALYTICS_BEGIN_CHECKOUT]: ({ payload }, prevState, nextState) => ({
    event: 'begin_checkout',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      ...(nextState.cart &&
        nextState.cart.promotionCoupon &&
        nextState.cart.promotionCoupon.code && { coupon: nextState.cart.promotionCoupon.code }),
      currency: selectCurrencyCode(nextState),
      value: transformPrice(payload.total, nextState),
      items: payload.items.map((cartProduct) => transformCartProduct(cartProduct, nextState)),
    },
  }),
  [ANALYTICS_PURCHASE]: ({ payload }, prevState, nextState) => ({
    event: 'purchase',
    ...(nextState.user.profile &&
      nextState.user.profile.id && { user_id: nextState.user.profile.id }),
    channel: nextState.channels.activeChannel,
    ecommerce: {
      ...(payload.promotionCoupon &&
        payload.promotionCoupon.code && { coupon: payload.promotionCoupon.code }),
      currency: selectCurrencyCode(nextState),
      transaction_id: payload.number,
      value: transformPrice(payload.total, nextState),
      items: payload.items.map((cartProduct) => transformCartProduct(cartProduct, nextState)),
    },
  }),
  [ANALYTICS_VIEW_ORDER_DETAILS]: ({ payload }, prevState, nextState) => ({
    event: 'view_order_details',
    payment_status: payload.paymentState,
    ecommerce: {
      ...(payload.promotionCoupon &&
        payload.promotionCoupon.code && { coupon: payload.promotionCoupon.code }),
      currency: selectCurrencyCode(nextState),
      transaction_id: payload.number,
      value: transformPrice(payload.total, nextState),
      items: payload.items.map((cartProduct) => transformCartProduct(cartProduct, nextState)),
    },
  }),
  [ANALYTICS_VIEW_PRODUCT_OR_CATEGORY]: ({ payload }) => ({
    page_category_game: makeTaxonList(payload)[0]
  }),
};

const middleware = createMiddleware(eventsMap, GoogleTagManager());

export default middleware;



// WEBPACK FOOTER //
// ./middlewares/analytics/gtm.js